/**
 * Phase 16F notification state for the staff app (REQ-NTF-01, 12, 12c, 13–15).
 *
 * Mounted inside the staff dashboard only, so it never runs in the guardian shell, the ops console,
 * the offline home / workspace or the lock screen (those replace the dashboard). It holds:
 * - the New count (badge), refetched on every push frame, every 60 s while the socket is down,
 *   and after my own actions;
 * - a per-tab list cache, loaded when the panel opens and refreshed when a push arrives;
 * - cross-tab updates: my actions in this tab tell my other tabs through a BroadcastChannel
 *   (the server push also reaches them; the channel covers a socket that is down).
 * The socket closes on sign-out, idle logout and lock (TokenService.onChange) and on unmount.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { NotificationTab, NotificationTaskInput, NotificationPushFrame } from '../dtos/notifications/notification.dto';
import type { NotificationItem } from '../dtos/notifications/notification.mapper';
import NotificationService, { NotificationActionResult, NotificationBulkAction } from '../services/notifications/notification.service';
import { NotificationSocket, notificationWsUrl, SocketStatus } from '../services/notifications/notificationSocket';
import { TokenService } from '../services/auth/token.service';
import { useCanPerform } from '../hooks/useCanPerform';
import { navigateToNotificationTarget } from '../components/notifications/navigateToTarget';

/** Cross-tab channel (no payload beyond the message type: no PHI). */
export const NOTIFICATION_CHANNEL = 'pelora-notifications';
/** sessionStorage key for a `/notifications/:id` deep link opened before the dashboard loaded. */
export const PENDING_NOTIFICATION_KEY = 'pelora-pending-notification';
/** Pending value for `/notifications` (no id): open the inbox. */
export const PENDING_INBOX = ':inbox';

export interface NotificationListState {
  items: NotificationItem[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
}

const EMPTY_LIST: NotificationListState = { items: [], loading: false, error: null, loaded: false };
const TABS: NotificationTab[] = ['new', 'acknowledged', 'cleared', 'resolved'];
const emptyLists = (): Record<NotificationTab, NotificationListState> =>
  ({ new: EMPTY_LIST, acknowledged: EMPTY_LIST, cleared: EMPTY_LIST, resolved: EMPTY_LIST });

export interface NotificationContextValue {
  enabled: boolean;
  canUpdate: boolean;
  count: number | null;
  countError: boolean;
  status: SocketStatus;
  lists: Record<NotificationTab, NotificationListState>;
  activeTab: NotificationTab;
  setActiveTab: (tab: NotificationTab) => void;
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  loadList: (tab: NotificationTab) => Promise<void>;
  refreshCount: () => Promise<void>;
  act: (action: NotificationBulkAction, ids: string[]) => Promise<NotificationActionResult>;
  completeTask: (notificationId: string, task: NotificationTaskInput) => Promise<void>;
  open: (item: NotificationItem) => void;
  /** Set when a deep link could not be opened (gone or not mine). */
  deepLinkError: string | null;
  clearDeepLinkError: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export interface NotificationProviderProps {
  children: React.ReactNode;
  /** Tests inject a fake socket factory. */
  socketFactory?: (options: ConstructorParameters<typeof NotificationSocket>[0]) => Pick<NotificationSocket, 'start' | 'stop'>;
}

const errorMessage = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children, socketFactory }) => {
  const enabled = useCanPerform('read:notifications');
  const canUpdate = useCanPerform('update:notifications');
  const [count, setCount] = useState<number | null>(null);
  const [countError, setCountError] = useState(false);
  const [status, setStatus] = useState<SocketStatus>('idle');
  const [lists, setLists] = useState<Record<NotificationTab, NotificationListState>>(emptyLists);
  const [activeTab, setActiveTab] = useState<NotificationTab>('new');
  const [panelOpen, setPanelOpen] = useState(false);
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);

  // Refs read by the socket callbacks, which outlive renders
  const panelOpenRef = useRef(panelOpen);
  const activeTabRef = useRef(activeTab);
  const mountedRef = useRef(true);
  const listRequestRef = useRef<Record<NotificationTab, number>>({ new: 0, acknowledged: 0, cleared: 0, resolved: 0 });
  panelOpenRef.current = panelOpen;
  activeTabRef.current = activeTab;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refreshCount = useCallback(async () => {
    try {
      const next = await NotificationService.getCount();
      if (!mountedRef.current) return;
      setCount(next);
      setCountError(false);
    } catch {
      if (mountedRef.current) setCountError(true);
    }
  }, []);

  const loadList = useCallback(async (tab: NotificationTab) => {
    const requestId = ++listRequestRef.current[tab];
    setLists((prev) => ({ ...prev, [tab]: { ...prev[tab], loading: true, error: null } }));
    try {
      const items = await NotificationService.list(tab);
      if (!mountedRef.current || requestId !== listRequestRef.current[tab]) return;
      setLists((prev) => ({ ...prev, [tab]: { items, loading: false, error: null, loaded: true } }));
    } catch (err) {
      if (!mountedRef.current || requestId !== listRequestRef.current[tab]) return;
      setLists((prev) => ({ ...prev, [tab]: { ...prev[tab], loading: false, error: errorMessage(err, 'Could not load notifications') } }));
    }
  }, []);

  /** Something changed: drop cached tabs and reload the one on screen. */
  const resync = useCallback((knownCount?: number) => {
    if (typeof knownCount === 'number') {
      setCount(Math.max(0, knownCount));
      setCountError(false);
    } else {
      void refreshCount();
    }
    setLists((prev) => {
      const next = { ...prev };
      for (const tab of TABS) next[tab] = { ...prev[tab], loaded: false };
      return next;
    });
    if (panelOpenRef.current) void loadList(activeTabRef.current);
  }, [refreshCount, loadList]);

  const resyncRef = useRef(resync);
  resyncRef.current = resync;

  // Socket lifecycle: open while enabled and signed in; close on sign-out / idle logout / lock
  useEffect(() => {
    if (!enabled) return undefined;
    void refreshCount();
    const options = {
      url: notificationWsUrl(),
      getTicket: () => NotificationService.getWsTicket(),
      onChanged: (frame: NotificationPushFrame) => resyncRef.current(frame.newCount),
      onPoll: () => { void refreshCount(); },
      onResync: () => resyncRef.current(),
      onStatus: (next: SocketStatus) => { if (mountedRef.current) setStatus(next); },
    };
    const socket = socketFactory ? socketFactory(options) : new NotificationSocket(options);
    socket.start();
    const unsubscribe = TokenService.onChange((authenticated) => {
      if (!authenticated) {
        socket.stop();
        try { sessionStorage.removeItem(PENDING_NOTIFICATION_KEY); } catch { /* storage unavailable */ }
      }
    });
    return () => {
      unsubscribe();
      socket.stop();
    };
  }, [enabled, refreshCount, socketFactory]);

  // Cross-tab: my actions in another tab
  const channelRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(NOTIFICATION_CHANNEL);
      channel.onmessage = (event: MessageEvent) => {
        if (event.data === 'changed') resyncRef.current();
      };
    } catch {
      channel = null; // unsupported: the server push and polling still update this tab
    }
    channelRef.current = channel;
    return () => {
      channel?.close();
      channelRef.current = null;
    };
  }, [enabled]);

  const act = useCallback(async (action: NotificationBulkAction, ids: string[]) => {
    const result = await NotificationService.act(action, ids);
    resync(result.newCount);
    try { channelRef.current?.postMessage('changed'); } catch { /* channel closed */ }
    return result;
  }, [resync]);

  const completeTask = useCallback(async (notificationId: string, task: NotificationTaskInput) => {
    await NotificationService.completeTask(notificationId, task);
    resync();
    try { channelRef.current?.postMessage('changed'); } catch { /* channel closed */ }
  }, [resync]);

  const open = useCallback((item: NotificationItem) => {
    setPanelOpen(false);
    navigateToNotificationTarget(item.target);
  }, []);

  // Deep link opened before the dashboard (e.g. from the email, after sign-in)
  useEffect(() => {
    if (!enabled) return;
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem(PENDING_NOTIFICATION_KEY);
      sessionStorage.removeItem(PENDING_NOTIFICATION_KEY);
    } catch {
      pending = null;
    }
    if (!pending) return;
    if (pending === PENDING_INBOX) {
      setActiveTab('new');
      setPanelOpen(true);
      return;
    }
    NotificationService.get(pending)
      .then((item) => {
        if (!mountedRef.current) return;
        if (item.status === 'RESOLVED' || !item.target) {
          setDeepLinkError(item.status === 'RESOLVED' ? 'This alert has already been resolved.' : 'This alert has no page to open.');
          setActiveTab(item.status === 'RESOLVED' ? 'resolved' : 'new');
          setPanelOpen(true);
          return;
        }
        navigateToNotificationTarget(item.target);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setDeepLinkError('This alert is no longer available.');
        setPanelOpen(true);
      });
  }, [enabled]);

  const clearDeepLinkError = useCallback(() => setDeepLinkError(null), []);

  const value = useMemo<NotificationContextValue>(() => ({
    enabled, canUpdate, count, countError, status, lists, activeTab, setActiveTab, panelOpen, setPanelOpen,
    loadList, refreshCount, act, completeTask, open, deepLinkError, clearDeepLinkError,
  }), [enabled, canUpdate, count, countError, status, lists, activeTab, panelOpen, loadList, refreshCount, act, completeTask, open, deepLinkError, clearDeepLinkError]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

/** Notification state; null outside the staff dashboard (the bell renders nothing then). */
export const useNotifications = (): NotificationContextValue | null => useContext(NotificationContext);

export default NotificationProvider;
